<template>
  <div
    v-if="(isInline || modelValue) && dashboard"
    :class="
      isInline
        ? 'dashboard-settings--inline dark'
        : 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'
    "
    :data-variant="isInline ? 'inline' : 'modal'">
    <div
      :class="
        isInline
          ? 'dashboard-settings__card'
          : 'bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col'
      ">
      <div v-if="!isInline" class="p-6 border-b dark:border-gray-700 flex-shrink-0">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Edit Dashboard</h2>
      </div>

      <div :class="isInline ? 'dashboard-settings__body' : 'p-6 space-y-4 overflow-y-auto flex-1'">
        <!-- Dashboard Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"> Dashboard Name </label>
          <input
            v-model="name"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            @keyup.enter="save" />
        </div>

        <!-- Icon Picker -->
        <IconPicker
          v-model="icon"
          :label="tt('Icon', 'Ikona')"
          fallback="dashboard"
          :placeholder="tt('Search icons', 'Išči ikone')" />

        <!-- What a LAYOUT is drawn on; a page has no type to change. The page
             kinds (`column`, `board`, `overview`, `grid`) are retired: an owner
             picks nothing, and a page's shape is its own business. -->
        <div v-if="isLayout">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {{ tt('Drawn on', 'Narisano na') }}
          </label>
          <select
            v-model="layoutType"
            :disabled="isHubPage"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60">
            <option value="canvas">{{ tt('Canvas', 'Platno') }}</option>
            <option v-if="canOfferBimLayout" value="bim">{{ tt('3D model', '3D model') }}</option>
          </select>
          <p v-if="isHubPage" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ tt('The hub’s 3D page keeps its layout — it is the one page that shows the home model.', '3D stran vozlišča obdrži svojo postavitev — to je edina stran, ki prikazuje model hiše.') }}
          </p>
          <p v-else-if="!canOfferBimLayout" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ tt('This hub already has its 3D page.', 'To vozlišče že ima svojo 3D stran.') }}
          </p>
          <p v-else class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{
              tt(
                'A canvas draws devices, text and images — and pipes, wires and ducts. Put a floor plan under it with the picture picker.',
                'Platno riše naprave, besedilo in slike — ter cevi, kable in kanale. Tloris položite pod njega z izbirnikom slike.',
              )
            }}
          </p>
        </div>

        <!-- Grid Settings -->
        <div v-if="layoutType === 'grid'" class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Columns</label>
            <input
              v-model.number="gridColumns"
              type="number"
              min="1"
              max="12"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">1-12 columns</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rows</label>
            <input
              v-model.number="gridRows"
              type="number"
              min="1"
              max="12"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">1-12 rows</p>
          </div>
        </div>

        <!-- Column Layout Settings -->
        <div v-if="layoutType === 'column' || layoutType === 'board'">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Columns</label>
          <input
            v-model.number="columnCount"
            type="number"
            min="1"
            max="6"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {{
              layoutType === 'board'
                ? tt('Board columns sit side by side and scroll sideways.', 'Stolpci plošče so drug ob drugem in se pomikajo vstran.')
                : tt('1-6 columns.', '1-6 stolpcev.')
            }}
          </p>
        </div>

        <!-- Hero placement: which automation pages show this layout, and where -->
        <div v-if="isLayout" class="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ tt('Show on these pages', 'Prikaži na teh straneh') }}
            </label>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {{ tt('The layout appears as the hero of the automation pages you tick.', 'Postavitev se prikaže kot glavni prikaz na izbranih straneh avtomatizacije.') }}
            </p>
          </div>

          <div class="grid grid-cols-2 gap-x-3 gap-y-1.5">
            <label
              v-for="category in heroCategoryOptions"
              :key="category.slug"
              class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                :value="category.slug"
                :checked="heroCategories.includes(category.slug)"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                @change="toggleHeroCategory(category.slug, ($event.target as HTMLInputElement).checked)" />
              {{ tt(category.label, category.label) }}
            </label>
          </div>

          <div v-if="heroCategories.length > 1" class="flex items-center gap-2">
            <label class="text-xs font-medium text-gray-600 dark:text-gray-300">
              {{ tt('Carousel order', 'Vrstni red') }}
            </label>
            <input
              v-model.number="heroOrder"
              type="number"
              min="0"
              max="99"
              class="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
            <span class="text-xs text-gray-500 dark:text-gray-400">
              {{ tt('lower slides first', 'nižje je prvo') }}
            </span>
          </div>
        </div>

        <!-- The HUB's 3D model (`t_hub_bim_model`), then the scan source: one
             model per hub, shared by every page, so neither is gated on this
             page being the 3D one. The old `layoutType === 'bim'` gate left
             every other page telling people to change a type it never offered —
             with "Use as model" and Delete out of reach for good. -->
        <div class="space-y-4 pt-2 border-t dark:border-gray-700">
          <!-- The HUB's model (`t_hub_bim_model`): there is one 3D model per hub,
               shared by everyone, so this block states that model and its facts
               rather than "this dashboard's". Replace feeds the file field BELOW
               (one pending file, one upload moment — Save), which is this dialog's
               idiom for every attachment it manages. -->
          <div class="rounded-lg border border-gray-200 dark:border-gray-700" data-testid="bim-dash-model">
            <div class="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ tt('Home 3D model (shared by this hub)', 'Domači 3D model (skupen za to vozlišče)') }}
              </span>
              <button
                type="button"
                data-testid="bim-dash-model-replace"
                class="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 dark:text-blue-400"
                :disabled="bimModelBusy !== '' || bimModelsLoading"
                @click="pickBimModelFile">
                <span class="material-symbols-outlined text-base">{{
                  dashBimModels.length > 0 ? 'swap_horiz' : 'upload_file'
                }}</span>
                {{ dashBimModels.length > 0 ? tt('Replace…', 'Zamenjaj …') : tt('Add model…', 'Dodaj model …') }}
              </button>
            </div>

            <p
              v-if="bimModelsLoading"
              data-bim-model-loading
              class="flex items-center gap-1 px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
              <span class="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></span>
              {{ tt('Reading the model…', 'Berem model …') }}
            </p>

            <p
              v-else-if="dashBimModels.length === 0"
              class="px-3 py-3 text-xs text-gray-500 dark:text-gray-400"
              data-bim-model-empty>
              {{
                tt(
                  'No model — the 3D view stays empty until one is uploaded.',
                  'Ni modela — 3D pogled ostane prazen, dokler modela ne naložite.',
                )
              }}
            </p>

            <ul v-else class="divide-y divide-gray-200 dark:divide-gray-700">
              <li
                v-for="row in dashBimModels"
                :key="row.id"
                class="flex items-start justify-between gap-3 px-3 py-3"
                data-bim-model-row
                :data-bim-model-id="row.id"
                :data-bim-model-active="row.active ? 'true' : 'false'">
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="truncate text-sm font-medium text-gray-900 dark:text-white">{{ row.name }}</span>
                    <span
                      v-if="row.active"
                      data-bim-model-active-badge
                      class="flex-shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      {{ tt('Active', 'Aktivni') }}
                    </span>
                  </div>
                  <div class="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span class="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-700 dark:text-gray-300">
                      {{ formatBytes(row.bytes) }}
                    </span>
                    <span v-if="bimModelUpdatedLabel(row)">
                      {{ tt('Updated', 'Posodobljeno') }}: {{ bimModelUpdatedLabel(row) }}
                    </span>
                  </div>
                </div>
                <div class="flex flex-shrink-0 gap-2">
                  <button
                    type="button"
                    data-testid="bim-dash-model-download"
                    class="flex items-center gap-1 rounded-lg bg-gray-200 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    :disabled="bimModelBusy !== ''"
                    @click="downloadBimModel(row)">
                    <span class="material-symbols-outlined align-middle text-base">download</span>
                    {{ tt('Download', 'Prenesi') }}
                  </button>
                  <button
                    type="button"
                    data-testid="bim-dash-model-delete"
                    class="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    :disabled="bimModelBusy !== ''"
                    @click="deleteBimModel(row)">
                    <span class="material-symbols-outlined align-middle text-base">delete</span>
                    {{ tt('Delete', 'Izbriši') }}
                  </button>
                </div>
              </li>
            </ul>

            <div
              v-if="bimModelBusy === 'download'"
              class="flex items-center gap-1 px-3 pb-3 text-xs text-gray-500 dark:text-gray-400">
              <div class="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></div>
              {{ tt('Fetching the model…', 'Prenašam model …') }}
            </div>
            <div
              v-if="bimModelError"
              data-bim-model-error
              class="px-3 pb-3 text-xs text-red-500">
              {{ bimModelError }}
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{
              tt('Model file', 'Datoteka modela')
            }}</label>
            <div
              class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-blue-500 transition-colors"
              :class="{ 'border-blue-500 bg-blue-50 dark:bg-blue-900/20': bimDragOver }"
              @dragover.prevent="bimDragOver = true"
              @dragleave="bimDragOver = false"
              @drop.prevent="handleBimFileDrop"
              @click="bimFileInput?.click()">
              <input ref="bimFileInput" type="file" :accept="BIM_MODEL_ACCEPT" class="hidden" @change="handleBimFileSelect" />
              <div v-if="!bimFile && !currentBimModelName">
                <span class="material-symbols-outlined text-3xl text-gray-400 mb-1 block">upload_file</span>
                <div class="text-sm text-gray-600 dark:text-gray-400">
                  {{ tt('Click or drag an IFC, GLB or glTF file here', 'Kliknite ali povlecite datoteko IFC, GLB ali glTF') }}
                </div>
                <div class="text-xs text-gray-500 mt-1">{{ tt('.ifc, .glb or .gltf (max 200MB)', '.ifc, .glb ali .gltf (največ 200 MB)') }}</div>
              </div>
              <div v-else>
                <span class="material-symbols-outlined text-3xl text-blue-500 mb-1 block">view_in_ar</span>
                <div class="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  {{ bimFile ? bimFile.name : currentBimModelName }}
                </div>
                <div class="text-xs text-gray-500 mt-1">
                  {{
                    bimFile
                      ? tt('New file selected — click Save to upload', 'Nova datoteka izbrana — kliknite Shrani za nalaganje')
                      : tt('Current model — click to replace', 'Trenutni model — kliknite za zamenjavo')
                  }}
                </div>
                <button v-if="bimFile" @click.stop="clearBimFile" class="mt-1 text-xs text-red-500 hover:text-red-600">
                  {{ tt('Remove', 'Odstrani') }}
                </button>
              </div>
            </div>
            <div v-if="bimUploading" class="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <div class="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></div>
              {{ tt('Uploading model…', 'Nalaganje modela …') }}
            </div>
            <div v-if="bimUploadError" class="mt-1 text-xs text-red-500">{{ bimUploadError }}</div>
          </div>
        </div>

        <!-- Scan 3D: capture a room, upload it to this hub, and use a scan as
             the hub's 3D model (card t_92f7411f). The choice is pending until
             Save, so Cancel costs nothing. -->
        <div class="pt-2 border-t dark:border-gray-700">
          <div v-if="scanAttaching" class="mb-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <div class="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></div>
            {{ t('views.scans.model.attaching') }}
          </div>
          <div v-if="scanAttachFailure" class="mb-2 text-xs text-red-500" data-scan-attach-error>
            {{ scanAttachFailure }}
          </div>
          <ScanModelSource
            :selected-scan-id="selectedScan?.id ?? null"
            :disabled="bimUploading || scanAttaching || bimModelBusy !== ''"
            @update:selected-scan="onScanSelected" />
        </div>

        <!-- Shared Layout Settings -->
        <div v-if="showSharedLayoutSettings" class="space-y-4 pt-2 border-t dark:border-gray-700">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Display Style</label>
            <select
              v-model="floorPlanDisplayMode"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="layout-only">Layout only</option>
              <option value="hero-below">Hero with columned page below</option>
              <option value="side-panel">Layout with side panel</option>
            </select>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Hero mode renders the main layout inline as part of the page. Side panel mode keeps the main layout beside
              a wider layout-controls panel.
            </p>
          </div>

          <div v-if="floorPlanDisplayMode === 'hero-below'">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Columns below hero</label>
            <input
              v-model.number="heroBelowColumns"
              type="number"
              min="1"
              max="4"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Used for layout controls rendered below the main layout.
            </p>
          </div>

          <!-- Aspect Ratio -->
          <div v-if="showAspectRatioSettings">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Canvas Size</label>
            <div class="space-y-1.5">
              <label
                v-for="preset in aspectRatioPresets"
                :key="preset.id"
                class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  :value="preset.id"
                  v-model="selectedPresetId"
                  @change="sizeMode = 'preset'"
                  class="h-4 w-4 text-blue-600" />
                <span class="text-sm text-gray-700 dark:text-gray-300">{{ preset.name }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="custom"
                  v-model="sizeMode"
                  @change="selectedPresetId = ''"
                  class="h-4 w-4 text-blue-600" />
                <span class="text-sm text-gray-700 dark:text-gray-300">Custom size</span>
              </label>
              <div v-if="sizeMode === 'custom'" class="grid grid-cols-2 gap-3 pl-6 mt-2">
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Width (px)</label>
                  <input
                    v-model.number="customWidth"
                    type="number"
                    min="100"
                    max="9999"
                    step="10"
                    class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Height (px)</label>
                  <input
                    v-model.number="customHeight"
                    type="number"
                    min="100"
                    max="9999"
                    step="10"
                    class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              <label
                v-if="layoutType === 'canvas' && currentFloorPlan"
                class="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="from-image"
                  v-model="sizeMode"
                  @change="selectedPresetId = ''"
                  class="h-4 w-4 text-blue-600" />
                <span class="text-sm text-gray-700 dark:text-gray-300">From image</span>
              </label>
              <p v-if="sizeMode === 'from-image'" class="text-xs text-gray-400 dark:text-gray-500 pl-6">
                Canvas dimensions will match the uploaded floor plan image.
              </p>
            </div>
          </div>

          <!-- Canvas Background Color -->
          <div v-if="layoutType === 'canvas'">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >Canvas Background Color</label
            >
            <div class="flex items-center gap-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" v-model="useBackgroundColor" class="h-4 w-4 text-blue-600 rounded" />
                <span class="text-sm text-gray-700 dark:text-gray-300">Set background color</span>
              </label>
              <input
                v-if="useBackgroundColor"
                v-model="backgroundColor"
                type="color"
                class="h-8 w-14 rounded border border-gray-300 dark:border-gray-600 cursor-pointer p-0.5" />
            </div>
          </div>

          <!-- The image UNDER a layout: a floor plan for the plan base, a
               process picture for the diagram base. One surface, one store. -->
          <div v-if="isLayout">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {{ tt('Background image', 'Slika v ozadju') }}
            </label>
            <FloorPlanImagePicker
              :selected-file="selectedFile"
              :preview-url="previewUrl"
              :current-image-url="currentFloorPlanImageUrl"
              :current-image-name="currentFloorPlan?.name || ''"
              confirm-action-label="Save"
              :disabled="floorPlanUploading"
              @update:selected-file="selectedFile = $event"
              @update:preview-url="previewUrl = $event"
              @remove="handleRemoveFloorPlan" />
            <div
              v-if="floorPlanUploading"
              class="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <div class="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></div>
              Uploading image...
            </div>
            <div v-if="uploadError" class="mt-1 text-xs text-red-500">{{ uploadError }}</div>
          </div>
        </div>

        <!-- Camera Background Settings -->
        <div class="pt-2 border-t dark:border-gray-700">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Camera Background</label>
          <select
            v-model="cameraBackgroundId"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-3">
            <option :value="null">None</option>
            <option v-for="camera in cameras" :key="camera.id" :value="camera.id">
              {{ camera.cameraName }}
            </option>
          </select>

          <div v-if="cameraBackgroundId" class="space-y-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Stream Type</label>
            <div class="flex gap-4">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="backgroundStreamType" value="snapshot" class="form-radio text-blue-500" />
                <span class="text-sm text-gray-700 dark:text-gray-300">Snapshot</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" v-model="backgroundStreamType" value="live" class="form-radio text-blue-500" />
                <span class="text-sm text-gray-700 dark:text-gray-300">Live Stream</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div
        :class="
          isInline
            ? 'dashboard-settings__footer'
            : 'p-6 border-t dark:border-gray-700 flex justify-between flex-shrink-0'
        ">
        <div class="flex gap-2">
          <button
            @click="deleteDashboard"
            class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
            Remove
          </button>
        </div>
        <div class="flex gap-2">
          <button
            v-if="!isInline"
            @click="$emit('update:modelValue', false)"
            class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button
            @click="save"
            :disabled="floorPlanUploading || scanAttaching || bimModelBusy !== ''"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors">
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/** The patch this modal sends to the dashboard store's update call. */
interface DashboardUpdatePayload {
  name?: string;
  icon?: string;
  layoutType?: string;
  cameraBackgroundId?: string | null;
  backgroundStreamType?: string | null;
  layoutConfig?: DashboardLayoutConfigLike;
  [key: string]: unknown;
}

import { ref, computed, watch, onMounted } from 'vue';
import { useDashboardStore } from '@/stores/dashboard';
import IconPicker from '@/components/inputs/IconPicker.vue';
import { tt } from '@/utils/consoleText';
import FloorPlanImagePicker from '@/components/dashboard/FloorPlanImagePicker.vue';
import { getAllPresets } from '@/constants/layoutPresets';
import { apiFetch } from '@/services/api';
import api from '@/services/api';
import { getLayoutDisplayMode, normalizeLayoutControlIds } from '@/utils/dashboardLayout';
import { BIM_MODEL_ACCEPT, bimModelFormatFromName, isBimModelUploadFormat, stripBimModelExtension } from '@/utils/bimModelFormat';
import { useI18n } from 'vue-i18n';
import { useMessageBox } from '@/composables/useMessageBox';
import { useAuthStore } from '@/stores/auth';
import { isAdminRole } from '@/utils/roles';
import { omitKeys } from '@/utils/omitKeys';
import ScanModelSource from '@/components/scan/ScanModelSource.vue';
import { attachScanModel, scanAttachError } from '@/services/scanModelAttach';
import type { HubScan } from '@/services/scanApi';
import { SCAN_ATTACH_SENTENCES } from '@/utils/scanTransferText';
import type { DashboardLayoutConfigLike } from '@/components/dashboard/dashboardTypes';
import { layoutConfigOf } from '@/components/dashboard/dashboardTypes';
import type { Dashboard } from '@/types';
import type { FloorPlanLike } from '@/components/dashboard/dashboardTypes';
import { dashboardBimModels, type DashboardBimModel } from '@/utils/bimDashboardModels';
import {
  BIM_MODELS_PATH,
  deleteHubBimModel,
  downloadHubBimModel,
  fetchHubBimModels,
} from '@/services/bimModelApi';
import { formatBytes } from '@/utils/bimDeviceModel';
import { formatDateTime } from '@/utils/formatDate';
import {
  isLayoutDashboard,
  layoutCanvasOf,
  layoutHeroCategories,
  layoutHeroOrder,
  LAYOUT_HERO_CATEGORIES,
  withLayoutHero,
} from '@/utils/layoutMarker';

const { t } = useI18n();
const { confirm: confirmBox } = useMessageBox();

interface Props {
  modelValue?: boolean;
  dashboard: Dashboard;
  /**
   * `inline` renders this SAME form inside the layout editor's panel instead of
   * a dialog: no overlay, no header, and only Save (a panel has nothing to
   * cancel). The owner asked for the page settings to live in the editor's
   * `Layout` tab, and one form is the only way the two cannot drift apart.
   */
  variant?: 'modal' | 'inline';
}

interface Camera {
  id: string;
  cameraName: string;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue', 'updated', 'deleted']);

/** The form as the layout editor's `Layout` tab, not as a dialog. */
const isInline = computed(() => props.variant === 'inline');

const dashboardStore = useDashboardStore();
const authStore = useAuthStore();
const name = ref('');
const icon = ref('dashboard');
const layoutType = ref<'column' | 'board' | 'overview' | 'canvas' | 'bim' | 'grid'>('column');

/** Is the row being edited a LAYOUT (a canvas), rather than a plain page? */
const isLayout = computed(() => isLayoutDashboard(props.dashboard));

/** The automation pages this layout is a hero for, and its slide order. */
const heroCategoryOptions = LAYOUT_HERO_CATEGORIES;
const heroCategories = ref<string[]>([]);
const heroOrder = ref(0);

function toggleHeroCategory(slug: string, checked: boolean): void {
  const next = heroCategories.value.filter((entry) => entry !== slug);
  if (checked) next.push(slug);
  heroCategories.value = LAYOUT_HERO_CATEGORIES.map((entry) => entry.slug).filter((entry) =>
    next.includes(entry),
  );
}

/**
 * The hub's shared 3D page (`t_hub_bim_model`): its layout is what makes it THE
 * 3D page, so the selector is locked, and no other page may become a second one
 * — the hub refuses either write with a 409.
 */
const isHubPage = computed(() => props.dashboard?.hubPage === true || props.dashboard?.userId == null);
const canOfferBimLayout = computed(
  () => isHubPage.value || (isAdminRole(authStore.user?.role) && !dashboardStore.hasHubBimDashboard),
);
const gridColumns = ref(12);
const gridRows = ref(4);
const columnCount = ref(2);
const cameras = ref<Camera[]>([]);
const cameraBackgroundId = ref<string | null>(null);
const backgroundStreamType = ref<'snapshot' | 'live'>('snapshot');

// Aspect ratio
const aspectRatioPresets = getAllPresets();
const selectedPresetId = ref('widescreen-16-9');
const sizeMode = ref<'preset' | 'custom' | 'from-image'>('preset');
const customWidth = ref(1920);
const customHeight = ref(1080);
// Canvas background color
const useBackgroundColor = ref(false);
const backgroundColor = ref('#1a2035');
const floorPlanDisplayMode = ref<'layout-only' | 'hero-below' | 'side-panel'>('layout-only');
const heroBelowColumns = ref(2);
const showSharedLayoutSettings = computed(
  () =>
    layoutType.value === 'canvas' ||
    layoutType.value === 'grid' ||
    layoutType.value === 'bim',
);
const showAspectRatioSettings = computed(
  () => layoutType.value === 'canvas' || layoutType.value === 'grid',
);

// BIM upload
const bimFileInput = ref<HTMLInputElement | null>(null);
const bimFile = ref<File | null>(null);
const bimDragOver = ref(false);
const bimUploading = ref(false);
const bimUploadError = ref('');
const currentBimModelName = ref('');
const currentBimModelId = ref('');

/**
 * The HUB's model rows (`t_hub_bim_model`), read from the same list the layout
 * reads — now with the facts the model-management block shows (name, size,
 * revision) and with the ACTIVE row stated, which is the row the layout renders
 * (`newestBimModelRow`). There is at most one.
 */
const dashBimModels = ref<DashboardBimModel[]>([]);
/** True while the hub's own list of this dashboard's models is in flight. */
const bimModelsLoading = ref(false);
/** One management action at a time: `download`, `delete`, or nothing. */
const bimModelBusy = ref<'' | 'download' | 'delete'>('');
const bimModelError = ref('');

/**
 * Scan 3D (card t_92f7411f): the scan chosen as this dashboard's model source,
 * and the attach that runs on Save. The choice is only ever PENDING while the
 * dialog is open — nothing reaches the hub until Save, so Cancel is free.
 */
const selectedScan = ref<HubScan | null>(null);
const scanAttaching = ref(false);
const scanAttachFailure = ref('');

/**
 * One sentence per attach failure code — this surface owns its language, and the
 * keys are shared with the model upload modal (`utils/scanTransferText.ts`).
 */
function scanAttachSentence(caught: unknown): string {
  const failed = scanAttachError(caught);
  const [key, fallback] = SCAN_ATTACH_SENTENCES[failed.code];
  return `${t(key, fallback)} — ${failed.detail}`;
}

/**
 * One model source at a time: a scan stands the picked file down, and a picked
 * file stands the scan down. The Save path then has no precedence to invent.
 */
function onScanSelected(scan: HubScan | null): void {
  selectedScan.value = scan;
  scanAttachFailure.value = '';
  if (!scan) return;
  bimFile.value = null;
  bimUploadError.value = '';
}

function handleBimFileSelect(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files?.[0]) processBimFile(input.files[0]);
}

function handleBimFileDrop(event: DragEvent) {
  bimDragOver.value = false;
  if (event.dataTransfer?.files?.[0]) processBimFile(event.dataTransfer.files[0]);
}

function processBimFile(file: File) {
  bimUploadError.value = '';
  // One import for the model kinds this DIALOG offers (card t_14696d19): an
  // IFC, or the GLB/glTF the scan pipeline emits. The loader sniffs the bytes
  // with the same rule — and it reads one format more than this picker offers
  // (a scan's USDZ arrives through the scan entry above, card t_92f7411f).
  if (!isBimModelUploadFormat(bimModelFormatFromName(file.name))) {
    bimUploadError.value = tt(
      'Please select an IFC (.ifc), GLB (.glb) or glTF (.gltf) file.',
      'Izberite datoteko IFC (.ifc), GLB (.glb) ali glTF (.gltf).',
    );
    return;
  }
  if (file.size > 200 * 1024 * 1024) {
    bimUploadError.value = tt('File size must be less than 200MB', 'Datoteka mora biti manjša od 200 MB');
    return;
  }
  bimFile.value = file;
  // One source at a time: a file picked here stands the scan down.
  selectedScan.value = null;
}

function clearBimFile() {
  bimFile.value = null;
  bimUploadError.value = '';
  if (bimFileInput.value) bimFileInput.value.value = '';
}

async function loadCurrentBimModel() {
  bimModelsLoading.value = true;
  try {
    const data = await fetchHubBimModels();
    const rows = dashboardBimModels(data);
    dashBimModels.value = rows;
    // The CURRENT model is the row the hub serves — the same rule the layout's
    // own loader uses, so the dialog and the scene cannot disagree.
    const active = rows.find((row) => row.active) ?? null;
    if (active) {
      currentBimModelName.value = active.name;
      currentBimModelId.value = active.id;
    } else {
      currentBimModelName.value = '';
      currentBimModelId.value = '';
    }
  } catch {
    dashBimModels.value = [];
    currentBimModelName.value = '';
    currentBimModelId.value = '';
  } finally {
    // The block must never say "no model" before the hub has answered: the read
    // is what states the empty case; a slow one is a spinner.
    bimModelsLoading.value = false;
  }
}

/** The revision time in the app's own locale, or `''` when the row names none. */
function bimModelUpdatedLabel(row: DashboardBimModel): string {
  if (!row.updatedAt) return '';
  const parsed = new Date(row.updatedAt);
  return Number.isNaN(parsed.getTime()) ? row.updatedAt : formatDateTime(parsed);
}

/** The hub's own word for a failed management action, else the block's own. */
function bimModelFailure(caught: unknown, fallback: string): string {
  const err = caught as {
    data?: { error?: string; message?: string; details?: string };
    message?: string;
  };
  return err?.data?.error || err?.data?.message || err?.message || fallback;
}

/**
 * The Replace/Add door of the model block: it opens the file field's own picker
 * (the drop zone below), because this dialog has ONE pending model file — the
 * upload itself runs on Save, in `save()`, exactly as it does for a file dragged
 * onto the field. A scan chosen meanwhile stands this file down, and vice versa.
 */
function pickBimModelFile(): void {
  bimModelError.value = '';
  bimFileInput.value?.click();
}

/**
 * Download the hub's model. The bytes come from the hub's own file route
 * (`GET /api/bim-models/:modelId/file`) through the app's API layer — the stable
 * `/api` path that works on every host (a hub-served page is same-origin,
 * Electron goes through otto-local, a cloud session through the WebRTC channel)
 * — and are handed to the platform (a browser download, or the iOS file opener),
 * which is the one save path the scans page already uses. A 19 MB IFC must not
 * become a button that quietly does nothing on a phone.
 */
async function downloadBimModel(row: DashboardBimModel): Promise<void> {
  if (bimModelBusy.value !== '' || !props.dashboard) return;
  bimModelBusy.value = 'download';
  bimModelError.value = '';
  try {
    await downloadHubBimModel({ id: row.id, name: row.name, s3Key: row.s3Key });
  } catch (__caught: unknown) {
    bimModelError.value = bimModelFailure(
      __caught,
      tt('The model could not be downloaded.', 'Modela ni bilo mogoče prenesti.'),
    );
  } finally {
    bimModelBusy.value = '';
  }
}

/**
 * Remove the HUB's model — the destructive action of the block, so it asks first
 * (the dialog's own rule for `Remove`, the dashboard itself) and then writes to
 * the hub at once: the row, its stored object (when nothing references it) and
 * its rooms sidecar are the hub's to clean up. The dialog re-reads the list and
 * tells its parent, so the empty state shows without waiting for the page's own
 * model poll.
 */
async function deleteBimModel(row: DashboardBimModel): Promise<void> {
  if (bimModelBusy.value !== '' || !props.dashboard) return;
  const ok = await confirmBox({
    message: tt(
      `Remove the hub's 3D model "${row.name}"? Every page that shows the house loses it, and the 3D view stays empty until another model is uploaded.`,
      `Odstranim 3D model vozlišča »${row.name}«? Izgubijo ga vse strani, ki prikazujejo hišo, 3D pogled pa ostane prazen, dokler ne naložite drugega modela.`,
    ),
    variant: 'danger',
  });
  if (!ok) return;

  bimModelBusy.value = 'delete';
  bimModelError.value = '';
  try {
    await deleteHubBimModel(row.id);
    dashBimModels.value = dashBimModels.value.filter((model) => model.id !== row.id);
    await loadCurrentBimModel();
    // The page owns the scene: it reloads the model (and finds none).
    emit('updated');
  } catch (__caught: unknown) {
    bimModelError.value = bimModelFailure(
      __caught,
      tt('The model could not be deleted.', 'Modela ni bilo mogoče izbrisati.'),
    );
  } finally {
    bimModelBusy.value = '';
  }
}

// Floor plan upload
const selectedFile = ref<File | null>(null);
const previewUrl = ref('');
const floorPlanUploading = ref(false);
const uploadError = ref('');
const currentFloorPlan = ref<FloorPlanLike | null>(null);
const pendingRemoveFloorPlan = ref(false);

function handleRemoveFloorPlan() {
  pendingRemoveFloorPlan.value = true;
  currentFloorPlan.value = null;
  selectedFile.value = null;
  previewUrl.value = '';
}

const currentFloorPlanImageUrl = computed(() => {
  if (!currentFloorPlan.value) return '';
  return currentFloorPlan.value?.imageUrl || currentFloorPlan.value?.imagePath || '';
});

async function loadCameras() {
  try {
    const response = await api.get('/cameras');
    cameras.value = response.data || response;
  } catch {
    cameras.value = [];
  }
}

async function loadCurrentFloorPlan(dashboardId: string) {
  try {
    const data = await apiFetch(`/layouts/${dashboardId}/floor-plans`);
    currentFloorPlan.value = Array.isArray(data) && data.length > 0 ? data[0] : null;
  } catch {
    currentFloorPlan.value = null;
  }
}

onMounted(() => {
  loadCameras();
});

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) {
      selectedFile.value = null;
      previewUrl.value = '';
      uploadError.value = '';
      // Cancel drops the pending model choice too — nothing was attached, and
      // reopening the dialog starts from the hub's own state. A management
      // action's note goes with it (the actions themselves already landed).
      selectedScan.value = null;
      scanAttachFailure.value = '';
      bimModelError.value = '';
    }
  },
);

watch(
  () => props.dashboard,
  (newDash) => {
    if (newDash) {
      name.value = newDash.name;
      icon.value = newDash.icon || 'dashboard';
      // The hub stores both of these as free strings; the dialog's own union is
      // what it offers in its controls and writes back.
      // A canvas is ONE type now (`layout`), so the select shows its BASE — the
      // thing the owner actually picks. Both 2D spellings answer `canvas`, and a
      // legacy row keeps its own type until it is saved again.
      const base = layoutCanvasOf(newDash);
      layoutType.value = (base === 'model' ? 'bim' : base === 'canvas' ? 'canvas' : newDash.layoutType || 'grid') as typeof layoutType.value;
      const lc = layoutConfigOf(newDash);
      gridColumns.value = Number(lc.columns) || 12;
      gridRows.value = Number(lc.rows) || 4;
      columnCount.value = Number(lc.columns) || 2;
      // Hero placement is the layout's own — read back so the checkboxes show
      // what the category page is actually drawing today.
      heroCategories.value = layoutHeroCategories(newDash.layoutConfig);
      heroOrder.value = layoutHeroOrder(newDash.layoutConfig);
      cameraBackgroundId.value = newDash.cameraBackgroundId || null;
      backgroundStreamType.value = (newDash.backgroundStreamType || 'snapshot') as typeof backgroundStreamType.value;

      // Aspect ratio
      if (lc.aspectRatioPresetId) {
        selectedPresetId.value = lc.aspectRatioPresetId;
        sizeMode.value = 'preset';
      } else if (lc.sizeFromImage) {
        sizeMode.value = 'from-image';
      } else if (lc.customWidth && lc.customHeight) {
        customWidth.value = lc.customWidth;
        customHeight.value = lc.customHeight;
        sizeMode.value = 'custom';
      } else if (lc.customRatioX && lc.customRatioY) {
        // Backward compat: convert legacy ratio to pixel dimensions (1920 base)
        const ar = lc.customRatioX / lc.customRatioY;
        if (ar >= 1) {
          customWidth.value = 1920;
          customHeight.value = Math.round(1920 / ar);
        } else {
          customWidth.value = Math.round(1920 * ar);
          customHeight.value = 1920;
        }
        sizeMode.value = 'custom';
      } else {
        selectedPresetId.value = 'widescreen-16-9';
        sizeMode.value = 'preset';
      }

      // Background color
      useBackgroundColor.value = Boolean(lc.backgroundColor);
      backgroundColor.value = lc.backgroundColor || '#1a2035';

      floorPlanDisplayMode.value = getLayoutDisplayMode(lc);
      heroBelowColumns.value = Math.max(1, Math.min(4, Number(lc.belowColumns) || 2));

      if (isLayout.value) {
        // Any layout base can carry an image: a floor plan for the plan base, a
        // process picture for the diagram base.
        pendingRemoveFloorPlan.value = false;
        loadCurrentFloorPlan(String(newDash.id));
      } else {
        currentFloorPlan.value = null;
      }

      // The model belongs to the HUB, not to this page, so this dialog reads it
      // for every page — the 3D one is only where it is shown big.
      dashBimModels.value = [];
      bimModelError.value = '';
      currentBimModelName.value = '';
      currentBimModelId.value = '';
      void loadCurrentBimModel();

      selectedFile.value = null;
      previewUrl.value = '';
      uploadError.value = '';
      bimFile.value = null;
      bimUploadError.value = '';
      selectedScan.value = null;
      scanAttachFailure.value = '';
    }
  },
  { immediate: true },
);

/**
 * The config this row already carries, when it is (or was) a layout.
 *
 * `layout` is the one type a canvas has carried since `t_layouts`, so the check is
 * the marker itself; `floorplan`/`scada` are the retired spellings a hub that has
 * not been migrated still answers.
 */
function getExistingSharedLayoutConfig() {
  if (!props.dashboard) return {};
  return isLayoutDashboard(props.dashboard) ||
    props.dashboard.layoutType === 'floorplan' ||
    props.dashboard.layoutType === 'scada' ||
    props.dashboard.layoutType === 'grid' ||
    props.dashboard.layoutType === 'bim'
    ? layoutConfigOf(props.dashboard)
    : {};
}

function buildSharedLayoutConfig(
  existingLayoutConfig: DashboardLayoutConfigLike = {},
  options: { includeAspectRatio?: boolean } = {},
) {
  const { includeAspectRatio = true } = options;
  const legacySecondaryControls = (existingLayoutConfig || {}).secondaryControls;
  const restLayoutConfig = omitKeys(existingLayoutConfig || {}, [
    'secondaryControls',
    'aspectRatioPresetId',
    'customRatioX',
    'customRatioY',
    'customWidth',
    'customHeight',
    'sizeFromImage',
    'backgroundColor',
  ]);
  const nextLayoutConfig: DashboardLayoutConfigLike = {
    ...restLayoutConfig,
    displayMode: floorPlanDisplayMode.value,
    belowColumns: Math.max(1, Math.min(4, heroBelowColumns.value || 2)),
    backgroundColor: useBackgroundColor.value ? backgroundColor.value : undefined,
  };

  if (includeAspectRatio) {
    if (sizeMode.value === 'preset') {
      Object.assign(nextLayoutConfig, {
        aspectRatioPresetId: selectedPresetId.value,
        customWidth: undefined,
        customHeight: undefined,
        sizeFromImage: undefined,
      });
    } else if (sizeMode.value === 'from-image') {
      Object.assign(nextLayoutConfig, {
        aspectRatioPresetId: undefined,
        customWidth: undefined,
        customHeight: undefined,
        sizeFromImage: true,
      });
    } else {
      // 'custom' — pixel dimensions
      Object.assign(nextLayoutConfig, {
        aspectRatioPresetId: undefined,
        customWidth: Math.max(100, Math.min(9999, customWidth.value || 1920)),
        customHeight: Math.max(100, Math.min(9999, customHeight.value || 1080)),
        sizeFromImage: undefined,
      });
    }
  }

  if (!Array.isArray(existingLayoutConfig?.layoutControls) && Array.isArray(legacySecondaryControls)) {
    nextLayoutConfig.layoutControls = normalizeLayoutControlIds(legacySecondaryControls);
  }

  return nextLayoutConfig;
}

async function save() {
  if (!name.value.trim() || !props.dashboard) return;

  try {
    const dashboardId = String(props.dashboard.id);

    // Upload the HUB's model if a file is pending (`t_hub_bim_model`). One model
    // per hub: the upload replaces it, and the hub carries the rooms across. The
    // model is the hub's, so this runs whatever this page's layout is.
    if (bimFile.value) {
      bimUploading.value = true;
      bimUploadError.value = '';
      try {
        const formData = new FormData();
        formData.append('bim_model', bimFile.value);
        formData.append('bim_model_name', stripBimModelExtension(bimFile.value.name) || 'BIM Model');
        await api.post(BIM_MODELS_PATH, formData);
        bimFile.value = null;
      } catch (__caught: unknown) {
        const err = __caught as {
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
          message?: string;
        };
        bimUploadError.value =
          err?.data?.error || err?.message || tt('Failed to upload the model', 'Modela ni bilo mogoče naložiti');
        bimUploading.value = false;
        return;
      } finally {
        bimUploading.value = false;
      }
    }

    // Attach the chosen scan as the hub's model (card t_92f7411f). It becomes
    // the SAME model row the upload above creates — built from the scan
    // package's own geometry (`model.glb` first, else the capture's `.usdz`).
    // One source at a time: a picked file stands the scan down before Save is
    // ever pressed. Like the upload, this is the hub's model, not this page's.
    if (selectedScan.value) {
      scanAttaching.value = true;
      scanAttachFailure.value = '';
      try {
        await attachScanModel(selectedScan.value);
        selectedScan.value = null;
      } catch (__caught: unknown) {
        scanAttachFailure.value = scanAttachSentence(__caught);
        return;
      } finally {
        scanAttaching.value = false;
      }
    }

    // Drop the image under the canvas. ANY canvas can carry one (a floor plan for
    // the plan of the house, a picture for a diagram), and the picker offers the
    // button on every base — so the delete must not be limited to one of them.
    if (pendingRemoveFloorPlan.value && isLayout.value) {
      floorPlanUploading.value = true;
      uploadError.value = '';
      try {
        await api.delete(`/layouts/${dashboardId}/floor-plans`);
        pendingRemoveFloorPlan.value = false;
      } catch (__caught: unknown) {
        const err = __caught as {
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
          message?: string;
        };
        uploadError.value = err?.data?.error || err?.message || 'Failed to remove floor plan';
        floorPlanUploading.value = false;
        return;
      } finally {
        floorPlanUploading.value = false;
      }
    }

    // Upload floor plan image if selected
    if (selectedFile.value && isLayout.value) {
      floorPlanUploading.value = true;
      uploadError.value = '';
      try {
        const formData = new FormData();
        formData.append('floor_plan', selectedFile.value);
        formData.append(
          'floor_plan_name',
          selectedFile.value.name.replace(/\.[^/.]+$/, '') ||
            (layoutType.value === 'canvas' ? 'Canvas' : 'Background'),
        );
        await api.post(`/layouts/${dashboardId}/floor-plans`, formData);
      } catch (__caught: unknown) {
        const err = __caught as {
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
          message?: string;
        };
        uploadError.value = err?.data?.error || err?.message || 'Failed to upload floor plan';
        floorPlanUploading.value = false;
        return;
      } finally {
        floorPlanUploading.value = false;
      }
    }

    const updates: DashboardUpdatePayload = {
      name: name.value,
      icon: icon.value,
      // A canvas is ONE type (`layout`) with a base; the 3D page keeps its own.
      layoutType: isLayout.value && layoutType.value !== 'bim' ? 'layout' : layoutType.value,
      cameraBackgroundId: cameraBackgroundId.value,
      backgroundStreamType: backgroundStreamType.value,
    };

    if (layoutType.value === 'grid') {
      updates.layoutConfig = {
        ...buildSharedLayoutConfig(getExistingSharedLayoutConfig()),
        columns: Math.max(1, Math.min(12, gridColumns.value || 12)),
        rows: Math.max(1, Math.min(12, gridRows.value || 4)),
      };
    } else if (layoutType.value === 'column' || layoutType.value === 'board') {
      // Spread the stored config rather than replacing it: a page that is also a
      // LAYOUT keeps the marker that makes it one (and the base it draws on).
      updates.layoutConfig = {
        ...layoutConfigOf(props.dashboard),
        columns: Math.max(1, Math.min(6, columnCount.value || (layoutType.value === 'board' ? 3 : 2))),
      };
    } else if (layoutType.value === 'canvas') {
      // The BASE is what changed: the document, the plan picture and the hero all
      // stay, only what the row is drawn on is rewritten.
      updates.layoutConfig = {
        ...buildSharedLayoutConfig(getExistingSharedLayoutConfig()),
        layout: {
          ...(layoutConfigOf(props.dashboard).layout as Record<string, unknown> | undefined),
          role: 'layout',
          version: 2,
          base: { kind: 'canvas' },
        },
      } as DashboardUpdatePayload['layoutConfig'];
    } else if (layoutType.value === 'bim') {
      updates.layoutConfig = buildSharedLayoutConfig(getExistingSharedLayoutConfig(), { includeAspectRatio: false });
    }

    // Which automation pages show this layout, and in what order — the one thing
    // that makes a layout visible outside its own route.
    if (isLayout.value && updates.layoutConfig) {
      updates.layoutConfig = withLayoutHero(updates.layoutConfig, {
        categories: heroCategories.value,
        order: heroOrder.value,
      }) as DashboardUpdatePayload['layoutConfig'];
    }

    await dashboardStore.updateDashboard(dashboardId, updates);

    emit('updated');
    emit('update:modelValue', false);
  } catch (err) {
    console.error('Error updating dashboard:', err);
  }
}

async function deleteDashboard() {
  if (!props.dashboard) return;
  const ok = await confirmBox(t('dialogs.dashboardDeleteConfirm', { name: props.dashboard.name }));
  if (!ok) return;

  try {
    const dashboardId = String(props.dashboard.id);
    await dashboardStore.deleteDashboard(dashboardId);
    emit('deleted');
    emit('update:modelValue', false);
  } catch (err) {
    console.error('Error deleting dashboard:', err);
  }
}
</script>

<style scoped>
/*
 * The INLINE variant (the layout editor's `Layout` tab). It is the dialog's own
 * body, so only the chrome changes: the panel already scrolls, and a Save is the
 * only thing the footer still needs. `dark` is set on the root by the template —
 * the editor panel is a dark surface whatever the app theme is.
 */
.dashboard-settings--inline {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.dashboard-settings__card {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
}

.dashboard-settings__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dashboard-settings__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
